import { motion } from "framer-motion";
import { ShoppingBag, Lock, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSearch } from "@/context/SearchContext";
import { resolveImage } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export const Header = ({ brand = "LUXOR", logo = "/logo.jpg" }) => {
  const { count, setOpen } = useCart();
  const { query, setQuery } = useSearch();
  const navigate = useNavigate();

  const goToResults = () => {
    const el = document.getElementById("injectable");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-9 left-0 right-0 z-50 backdrop-blur-xl bg-[#050805]/80 border-b border-white/10"
      data-testid="site-header"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("top")}
          className="flex items-center transition-opacity duration-300 hover:opacity-80"
          data-testid="brand-logo"
        >
          <img src={resolveImage(logo)} alt={brand} className="h-14 w-14 md:h-16 md:w-16 object-cover object-center rounded-md" />
        </button>

        <nav className="hidden md:flex items-center gap-12 font-body text-xs uppercase tracking-[0.25em] text-zinc-400">
          <button onClick={() => scrollTo("injectable")} className="hover:text-white transition-colors duration-300" data-testid="nav-injectable">Injectable</button>
          <button onClick={() => scrollTo("oral")} className="hover:text-white transition-colors duration-300" data-testid="nav-oral">Oral</button>
        </nav>

        <div className="flex items-center gap-4 md:gap-6">
          <div className="flex items-center gap-2 border border-[#29FF87]/25 rounded-full px-3 sm:px-4 py-2 focus-within:border-[#29FF87] transition-colors duration-300">
            <Search size={15} className="text-[#29FF87] shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") goToResults(); }}
              placeholder="Search..."
              className="bg-transparent outline-none font-body text-sm text-white placeholder:text-[#7BC79A]/50 w-24 sm:w-36 lg:w-48"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => navigate("/admin")}
            className="text-zinc-500 hover:text-[#29FF87] transition-colors duration-300"
            data-testid="admin-link"
            aria-label="Admin"
          >
            <Lock size={18} strokeWidth={1.4} />
          </button>
          <button
            onClick={() => setOpen(true)}
            className="relative group flex items-center gap-2 text-white"
            data-testid="cart-button"
          >
            <ShoppingBag size={20} strokeWidth={1.4} className="group-hover:text-[#29FF87] transition-colors duration-300" />
            {count > 0 && (
              <span className="absolute -top-2 -right-3 bg-[#29FF87] text-[#050805] text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
};
